// src/documents/xlsx/parseXlsxBorders.js
//
// SheetJS Community xlsx okumasında border'lar cell.s.border'a yansımıyor
// (sadece patternType/fgColor okunuyor). Renderer'ın border'ları görmesi için
// xlsx ZIP'inden styles.xml ve sheet xml'i manuel parse edip her hücrenin
// border bilgisini çıkarıyoruz.

import JSZip from 'jszip';

function extractSide(inner, side) {
  // <SIDE/> (no border) | <SIDE style="STYLE"><color .../></SIDE> (border)
  const reFull = new RegExp(`<${side}\\s+style="([^"]+)"[^>]*?>([\\s\\S]*?)</${side}>`);
  const reSelf = new RegExp(`<${side}\\s+style="([^"]+)"[^>]*?/>`);
  const m = inner.match(reFull);
  if (m) {
    const style = m[1];
    if (!style || style === 'none') return null;
    // Inner içeriğinde <color rgb="FF000000"/> ya da <color auto="1"/> ara
    const inn = m[2] || '';
    const rgbM = inn.match(/<color[^>]*\brgb="([0-9A-Fa-f]+)"/);
    let rgb;
    if (rgbM) {
      // ARGB ise alpha'yı at — sadece RGB tut
      const v = rgbM[1].toUpperCase();
      rgb = v.length === 8 ? v.slice(2) : v;
    }
    return rgb ? { style, color: { rgb } } : { style };
  }
  const ms = inner.match(reSelf);
  if (ms) {
    const style = ms[1];
    if (style && style !== 'none') return { style };
  }
  return null;
}

function parseBorders(stylesXml) {
  const block = stylesXml.match(/<borders[^>]*>([\s\S]*?)<\/borders>/);
  if (!block) return [];
  const borders = [];
  // Her <border>...</border> entry'si
  const re = /<border[^>]*>([\s\S]*?)<\/border>/g;
  let m;
  while ((m = re.exec(block[1])) !== null) {
    const inner = m[1];
    borders.push({
      top:    extractSide(inner, 'top'),
      right:  extractSide(inner, 'right'),
      bottom: extractSide(inner, 'bottom'),
      left:   extractSide(inner, 'left'),
    });
  }
  return borders;
}

function parseCellXfs(stylesXml) {
  const block = stylesXml.match(/<cellXfs[^>]*>([\s\S]*?)<\/cellXfs>/);
  if (!block) return [];
  const xfs = [];
  const re = /<xf[^>]*?\/?>/g;
  let m;
  while ((m = re.exec(block[1])) !== null) {
    const tag = m[0];
    const borderIdM = tag.match(/borderId="(\d+)"/);
    xfs.push({
      borderId: borderIdM ? parseInt(borderIdM[1], 10) : 0,
    });
  }
  return xfs;
}

/**
 * @param {ArrayBuffer|Uint8Array} xlsxBytes
 * @returns {Promise<Map<string, {top?,right?,bottom?,left?}>>}
 *   Map cell address (örn. "A1") → { top: {style}, right: ..., bottom: ..., left: ... }
 */
export async function parseXlsxBorders(xlsxBytes) {
  try {
    const zip = await JSZip.loadAsync(xlsxBytes);
    const stylesXml = await zip.file('xl/styles.xml')?.async('string');
    if (!stylesXml) return new Map();

    const borders = parseBorders(stylesXml);
    const cellXfs = parseCellXfs(stylesXml);

    const sheetFiles = zip.file(/xl\/worksheets\/sheet\d+\.xml/);
    if (!sheetFiles || sheetFiles.length === 0) return new Map();
    const sheetXml = await sheetFiles[0].async('string');

    const cellBorders = new Map();
    // <c r="A1" s="3" .../> şeklindeki tüm hücreler
    const cellRe = /<c\s+r="([A-Z]+\d+)"([^>]*?)(?:\/>|>)/g;
    let m;
    while ((m = cellRe.exec(sheetXml)) !== null) {
      const addr = m[1];
      const attrs = m[2];
      const sM = attrs.match(/\bs="(\d+)"/);
      const sIdx = sM ? parseInt(sM[1], 10) : 0;
      const xf = cellXfs[sIdx];
      if (!xf) continue;
      const border = borders[xf.borderId];
      if (!border) continue;
      // En az bir kenarı varsa map'e ekle
      if (border.top || border.right || border.bottom || border.left) {
        cellBorders.set(addr, border);
      }
    }

    return cellBorders;
  } catch (err) {
    console.warn('[parseXlsxBorders] hata:', err);
    return new Map();
  }
}
